import { cn, hasUserMessage } from '@/lib/utils';
import {
  MutableRefObject,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  ChainlitContext,
  FileSpec,
  useChatMessages,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

import { Markdown } from '@/components/Markdown';

import AmbientSquares from './AmbientSquares';
import MessageComposer from './MessageComposer';
import Starters from './Starters';
import ThinkingPreview from './ThinkingPreview';

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
}

export default function WelcomeScreen(props: Props) {
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const { chatProfile } = useChatSession();
  const { messages } = useChatMessages();
  const [isVisible, setIsVisible] = useState(false);
  const isThinkingPreview =
    new URLSearchParams(window.location.search).get('preview') === 'thinking';

  const chatProfiles = config?.chatProfiles;

  const allowHtml = config?.features?.unsafe_allow_html;
  const latex = config?.features?.latex;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const profileIntro = useMemo(() => {
    if (chatProfile && chatProfiles) {
      const currentChatProfile = chatProfiles.find(
        (cp) => cp.name === chatProfile
      );
      if (currentChatProfile?.icon) {
        return (
          <div className="flex flex-col gap-2 mb-2 items-center">
            <img
              className="size-16 rounded-2xl border border-border/70 shadow-sm"
              src={
                currentChatProfile?.icon.startsWith('/public')
                  ? apiClient.buildEndpoint(currentChatProfile?.icon)
                  : currentChatProfile?.icon
              }
            />
            {currentChatProfile?.markdown_description ? (
              <Markdown allowHtml={allowHtml} latex={latex}>
                {currentChatProfile.markdown_description}
              </Markdown>
            ) : null}
          </div>
        );
      }
    }

    return null;
  }, [chatProfiles, chatProfile]);

  if (hasUserMessage(messages)) return null;

  return (
    <div
      id="welcome-screen"
      className={cn(
        'welcome-screen relative isolate mx-auto flex w-full max-w-[42rem] flex-grow translate-y-1 flex-col items-stretch justify-start overflow-visible text-left opacity-0 transition-[opacity,transform] duration-200 ease-out',
        isThinkingPreview
          ? 'gap-0 pb-0 pt-0'
          : 'gap-5 pb-10 pt-[clamp(3.5rem,11vh,6.5rem)]',
        isVisible && 'translate-y-0 opacity-100'
      )}
    >
      {!isThinkingPreview ? <AmbientSquares /> : null}
      {isThinkingPreview ? (
        <ThinkingPreview />
      ) : (
        <>
          {profileIntro ? (
            <div className="relative z-10">{profileIntro}</div>
          ) : null}
          <div className="relative z-10 space-y-2">
            <h1 className="text-[28px] font-bold leading-9 tracking-[-0.035em] text-foreground sm:text-[32px]">
              What can I help with?
            </h1>
            <p className="max-w-lg text-sm leading-6 text-foreground/70 sm:text-[15px]">
              Guidance on IPMA certification, ICB4, events and membership.
            </p>
          </div>
          <Starters className="relative z-10" />
        </>
      )}
      <div
        className={cn(
          'relative z-10 w-full pt-0.5',
          isThinkingPreview && 'mt-auto'
        )}
      >
        <MessageComposer {...props} variant="compact" />
        <p className="mt-2 text-[11px] text-muted-foreground/70">
          Press Enter to send · Shift + Enter for a new line
        </p>
      </div>
    </div>
  );
}
